import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  Alert,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  CurrencyTextField,
  type CustomFieldValidatorState,
  ErrorTooltip,
  moment,
  type Nil,
  PuiTextArea,
  Text,
  TextWithTooltip,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'
import { Eye as EyeIcon } from '@pbt/pbt-ui-components/src/icons'
import { formatMoney } from '@pbt/pbt-ui-components/src/utils/numberUtils'

import { OutlinedSelect } from '~/components/elements/OutlinedSelect/OutlinedSelect'
import FeatureToggle from '~/constants/featureToggle'
import { WpCancelPaymentType } from '~/constants/paymentTypes'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { DataHandle, Membership } from '~/types'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { useWellnessPlanCancelReasons } from '../../wellness-plans/wellnessPlanUtils'
import {
  type CancelData,
  CancelTiming,
  createChargeClientAmountValidator,
  MIN_CLIENT_CHARGE_AMOUNT,
} from './cancelMembershipUtils'

const StyledAlert = styled(Alert)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  border: solid 1px;
  border-color: ${({ theme }) => theme.palette.info.light};
`

const StyledAlertText = styled(Text)`
  && {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`

const WarningAlert = styled(Alert)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  border: solid 1px;
  border-color: ${({ theme }) => theme.palette.warning.dark};
`

const WarningAlertText = styled(Text)`
  && {
    color: ${({ theme }) => theme.palette.warning.dark};
  }
`

const DetermineRefundOrChargeSection = styled('div')`
  margin-top: ${({ theme }) => theme.spacing(2)};
  width: calc(50% - ${({ theme }) => theme.spacing(2)});
`

const CancellationSummaryContainer = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  border-top: none;
`

const CancellationSummarySection = styled('div')`
  flex: 1;
`

const SummaryRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1, 0)};

  &:last-child {
    border-bottom: none;
  }
`

const ChargeOrRefundRow = styled('div')`
  display: flex;
  align-items: center;
`

const LabelWithIcon = styled('div')`
  display: flex;
  align-items: center;
`

const CompactIconButton = styled(IconButton)`
  padding: 0;
  height: 21px;
`

const TotalRow = styled(SummaryRow)`
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  border-bottom: none;
`

const ErrorText = styled(Text)`
  && {
    color: ${({ theme }) => theme.palette.error.main};
  }
`

const StyledCurrencyTextField = styled(CurrencyTextField)`
  margin-left: auto;
  width: 85px;

  & .MuiInput-underline:before,
  & .MuiInput-underline:after {
    bottom: 6px;
  }
`

interface CancelMembershipDetailsProps {
  cancelFailureAlert?: React.ReactNode
  membership?: Membership | Nil
  onViewTotalPaid: () => void
  onViewTotalUsed: () => void
}

const CancelMembershipDetails = forwardRef<
  DataHandle<CancelData> | undefined,
  CancelMembershipDetailsProps
>(
  /* NOSONAR */
  function CancelMembershipDetails(
    { cancelFailureAlert, membership, onViewTotalPaid, onViewTotalUsed },
    ref,
  ) {
    const { t } = useTranslation([
      'Common',
      'Dialogs',
      'Memberships',
      'Tooltips',
      'Validations',
    ])

    const timezone = useCurrentBusinessTimezone()

    const WellnessPlanCancelReasons: string[] = useWellnessPlanCancelReasons()

    const isRefund = membership?.cancellationType === WpCancelPaymentType.REFUND
    const isCharge = membership?.cancellationType === WpCancelPaymentType.CHARGE
    const cancellationAmount = membership?.cancellationAmount ?? 0

    const isM2bBraintreeSignupEnabled = useSelector(
      getFeatureToggle(FeatureToggle.IPO_M2B_BRAINTREE_SIGNUP),
    )
    const isCurrentBusinessOmniChannel = useSelector(
      getCurrentBusinessIsOmniChannel,
    )
    const shouldValidateChargeClientAmount =
      !isCurrentBusinessOmniChannel && !isM2bBraintreeSignupEnabled

    const chargeClientAmountValidator =
      createChargeClientAmountValidator(isCharge)

    const validateNotesWhenOtherReasonSelected = ({
      state,
    }: CustomFieldValidatorState) => {
      if (state?.reason !== t('Common:OTHER')) {
        return true
      }
      return state?.notes?.trim().length > 0
    }

    const {
      fields: {
        cancelTiming,
        reason,
        notes,
        chargeClient,
        writeOff,
        refundClient,
      },
      validate,
      reset,
    } = useFields(
      [
        { name: 'cancelTiming', initialValue: CancelTiming.IMMEDIATELY },
        {
          name: 'reason',
          initialValue: '',
          validators: ['required'],
          messages: {
            required: t('Validations:SELECT_CANCELLATION_REASON'),
          },
        },
        {
          name: 'notes',
          initialValue: '',
          validators: [
            {
              validator: validateNotesWhenOtherReasonSelected,
              validatorName: 'required',
            },
          ],
          messages: {
            required: t('Validations:ENTER_CANCELLATION_NOTES'),
          },
        },
        {
          name: 'chargeClient',
          initialValue: cancellationAmount,
          validators: shouldValidateChargeClientAmount
            ? [
                {
                  validator: chargeClientAmountValidator,
                  validatorName: 'chargeClientAmountValidator',
                },
              ]
            : [],
          messages: shouldValidateChargeClientAmount
            ? {
                chargeClientAmountValidator: t(
                  'Validations:MIN_CHARGE_AMOUNT',
                  {
                    amount: formatMoney(MIN_CLIENT_CHARGE_AMOUNT),
                  },
                ),
              }
            : {},
        },
        { name: 'writeOff', initialValue: 0 },
        { name: 'refundClient', initialValue: cancellationAmount },
      ],
      false,
    )

    useEffect(() => {
      reset()
    }, [cancellationAmount])

    useEffect(() => {
      writeOff.setValue(Utils.round(cancellationAmount - chargeClient.value, 2))
    }, [chargeClient.value])

    useEffect(() => {
      chargeClient.setValue(Utils.round(cancellationAmount - writeOff.value, 2))
    }, [writeOff.value])

    useEffect(() => {
      const currentValue = refundClient.value
      const roundedValue = Utils.round(Number(currentValue) || 0, 2)
      if (currentValue !== roundedValue) {
        refundClient.setValue(roundedValue)
      }
    }, [refundClient.value])

    const FieldsLabels = {
      REASON_FOR_CANCELLATION: `${t('Memberships:CANCEL_PLAN.REASON_FOR_CANCELLATION')}*`,
      CANCELLATION_NOTES: `${t('Memberships:CANCEL_PLAN.CANCELLATION_NOTES')}*`,
    }

    useImperativeHandle(ref, () => ({
      validate,
      get: () => ({
        cancelTiming: cancelTiming.value,
        chargeClient: chargeClient.value,
        notes: notes.value,
        reason: reason.value,
        refundClient: refundClient.value,
        writeOff: writeOff.value,
      }),
    }))

    const planEndDate = membership?.plans?.[0]?.period?.endDate
    const momentEndDate = planEndDate ? moment.tz(planEndDate, timezone) : null
    const formattedEndDate = momentEndDate?.isValid()
      ? momentEndDate.format('MM/DD/YYYY')
      : ''
    const isEndOfTermCancellation =
      cancelTiming.value === CancelTiming.END_OF_TERM
    const showCancelAtEndOfTermBanner =
      isEndOfTermCancellation && formattedEndDate
    const showRefundOrChargeInputs =
      (isRefund || isCharge) && !isEndOfTermCancellation
    const showCancellationSummary = !isEndOfTermCancellation

    const remainingTermMonths = momentEndDate?.isValid()
      ? Math.max(
          0,
          momentEndDate
            .clone()
            .startOf('day')
            .diff(moment.tz(timezone).startOf('day'), 'months'),
        )
      : 0
    const showFinalMonthWarning =
      !isEndOfTermCancellation && remainingTermMonths === 0 && formattedEndDate
    const planPricing = membership?.price?.price ?? 0
    const isYearlyPricing = membership?.price?.priceType?.name === 'ANNUAL'
    const priceInterval = isYearlyPricing
      ? t('Constants:TIME_UNITS.YEAR_ONE').toLowerCase()
      : t('Constants:TIME_UNITS.MONTH_ONE').toLowerCase()
    const nextPaymentDue = membership?.plans?.[0]?.nextPaymentDue
    const momentNextPaymentDue = nextPaymentDue
      ? moment.tz(nextPaymentDue, timezone)
      : null
    // For annual: year is paid upfront, remaining = $0
    // For monthly: calculate months from nextPaymentDue to endDate (excludes current paid month)
    const remainingPaymentMonths =
      momentNextPaymentDue?.isValid() && momentEndDate?.isValid()
        ? Math.max(
            0,
            momentEndDate
              .clone()
              .startOf('day')
              .diff(momentNextPaymentDue.clone().startOf('day'), 'months'),
          )
        : 0
    const remainingCost = isYearlyPricing
      ? 0
      : remainingPaymentMonths * planPricing
    const totalPaidAmount = membership?.totalPaidAmount ?? 0
    const totalUsedAmount = membership?.totalUsedAmount ?? 0
    const totalRefundedAmount = membership?.totalRefundedAmount ?? 0

    const totalUsage = Utils.round(
      Math.abs(totalPaidAmount - (totalUsedAmount + totalRefundedAmount)),
      2,
    )

    const chargeClientTooltip = (
      <Trans
        components={{ br: <br /> }}
        i18nKey="Tooltips:CANCEL_MEMBERSHIP_CHARGE_CLIENT_V2"
      />
    )

    const writeOffTooltip = (
      <Trans
        components={{ br: <br /> }}
        i18nKey="Tooltips:CANCEL_MEMBERSHIP_WRITE_OFF_V2"
      />
    )

    return (
      <Grid container direction="column" p={3}>
        <Text strong mb={1} variant="subheading3">
          {t('Memberships:CANCEL_PLAN.CANCEL_TIMING')}
        </Text>
        <RadioGroup
          row
          value={cancelTiming.value}
          onChange={(_, value) => cancelTiming.setValue(value as CancelTiming)}
        >
          <FormControlLabel
            control={<Radio />}
            label={t('Memberships:CANCEL_PLAN.IMMEDIATELY')}
            value={CancelTiming.IMMEDIATELY}
          />
          <FormControlLabel
            control={<Radio />}
            label={t('Memberships:CANCEL_PLAN.AT_END_OF_TERM')}
            value={CancelTiming.END_OF_TERM}
          />
        </RadioGroup>
        {cancelFailureAlert && (
          <Grid container mb={2} mt={2}>
            {cancelFailureAlert}
          </Grid>
        )}
        {showCancelAtEndOfTermBanner && (
          <StyledAlert severity="info">
            <StyledAlertText strong variant="body2">
              {t('Memberships:CANCEL_PLAN.PLAN_WILL_CANCEL_ON', {
                date: formattedEndDate,
              })}
            </StyledAlertText>
            <StyledAlertText variant="body2">
              {t('Memberships:CANCEL_PLAN.BENEFITS_REMAIN_ACTIVE')}
            </StyledAlertText>
          </StyledAlert>
        )}
        {showFinalMonthWarning && (
          <WarningAlert severity="warning">
            <WarningAlertText strong variant="body2">
              {t('Memberships:CANCEL_PLAN.FINAL_MONTH_TITLE')}
            </WarningAlertText>
            <WarningAlertText variant="body2">
              {t('Memberships:CANCEL_PLAN.FINAL_MONTH_DESCRIPTION')}
            </WarningAlertText>
          </WarningAlert>
        )}
        {showRefundOrChargeInputs && (
          <DetermineRefundOrChargeSection>
            {isRefund ? (
              <>
                <Text strong mb={1} variant="subheading3">
                  {t('Memberships:CANCEL_PLAN.DETERMINE_REFUND_AMOUNT')}
                </Text>
                <ChargeOrRefundRow>
                  <Text variant="body2">
                    {t('Common:PAYMENTS.REFUND_AMOUNT')}
                  </Text>
                  <StyledCurrencyTextField
                    field={refundClient}
                    max={cancellationAmount}
                  />
                </ChargeOrRefundRow>
              </>
            ) : (
              <>
                <Text strong mb={1} variant="subheading3">
                  {t('Memberships:CANCEL_PLAN.DETERMINE_FINAL_CHARGE')}
                </Text>
                <ChargeOrRefundRow>
                  <TextWithTooltip
                    tooltipText={chargeClientTooltip}
                    variant="body2"
                  >
                    {t('Dialogs:CANCEL_MEMBERSHIP_DIALOG.CHARGE_CLIENT')}
                  </TextWithTooltip>
                  <StyledCurrencyTextField
                    field={chargeClient}
                    max={cancellationAmount}
                  />
                </ChargeOrRefundRow>
                <ChargeOrRefundRow>
                  <TextWithTooltip
                    tooltipText={writeOffTooltip}
                    variant="body2"
                  >
                    {t('Dialogs:CANCEL_MEMBERSHIP_DIALOG.WRITE_OFF')}
                  </TextWithTooltip>
                  <StyledCurrencyTextField
                    field={writeOff}
                    max={cancellationAmount}
                  />
                </ChargeOrRefundRow>
              </>
            )}
          </DetermineRefundOrChargeSection>
        )}
        {showCancellationSummary && (
          <CancellationSummaryContainer>
            <CancellationSummarySection>
              <Text strong mb={1} variant="subheading3">
                {t('Memberships:CANCEL_PLAN.PLAN_USAGE_VALUE')}
              </Text>
              <SummaryRow>
                <LabelWithIcon>
                  <Text variant="body2">
                    {t('Dialogs:CANCEL_MEMBERSHIP_DIALOG.TOTAL_USED')}
                  </Text>
                  <CompactIconButton
                    aria-label={t(
                      'Memberships:CANCEL_PLAN.VIEW_TOTAL_USAGE_DETAILS',
                    )}
                    size="small"
                    onClick={onViewTotalUsed}
                  >
                    <EyeIcon />
                  </CompactIconButton>
                </LabelWithIcon>
                <Text variant="body2">{formatMoney(totalUsedAmount)}</Text>
              </SummaryRow>
              <SummaryRow>
                <LabelWithIcon>
                  <Text variant="body2">
                    {t('Dialogs:CANCEL_MEMBERSHIP_DIALOG.TOTAL_PAID')}
                  </Text>
                  <CompactIconButton
                    aria-label={t(
                      'Memberships:CANCEL_PLAN.VIEW_TOTAL_PAYMENTS_DETAILS',
                    )}
                    size="small"
                    onClick={onViewTotalPaid}
                  >
                    <EyeIcon />
                  </CompactIconButton>
                </LabelWithIcon>
                <ErrorText variant="body2">
                  -{formatMoney(totalPaidAmount)}
                </ErrorText>
              </SummaryRow>
              <SummaryRow>
                <Text variant="body2">
                  {t('Common:PAYMENTS.AMOUNT_REFUNDED')}
                </Text>
                <Text variant="body2">{formatMoney(totalRefundedAmount)}</Text>
              </SummaryRow>
              <TotalRow>
                <Text strong variant="body2">
                  {t('Memberships:CANCEL_PLAN.TOTAL_USAGE')}
                </Text>
                <Text strong data-testid="total-usage-value" variant="body2">
                  {formatMoney(totalUsage)}
                </Text>
              </TotalRow>
            </CancellationSummarySection>
            <CancellationSummarySection>
              <Text strong mb={1} variant="subheading3">
                {t('Memberships:CANCEL_PLAN.REMAINING_MEMBERSHIP_COST')}
              </Text>
              <SummaryRow>
                <Text variant="body2">
                  {t('Memberships:CANCEL_PLAN.REMAINING_TERM')}
                </Text>
                <Text variant="body2">
                  {t('Memberships:CANCEL_PLAN.REMAINING_TERM_MONTHS', {
                    count: isYearlyPricing
                      ? remainingTermMonths
                      : remainingPaymentMonths,
                  })}
                </Text>
              </SummaryRow>
              <SummaryRow>
                <Text variant="body2">
                  {t('Memberships:CANCEL_PLAN.PLAN_PRICING')}
                </Text>
                <Text variant="body2">
                  {formatMoney(planPricing)}/{priceInterval}
                </Text>
              </SummaryRow>
              <SummaryRow sx={{ visibility: 'hidden' }}>
                <Text variant="body2">&nbsp;</Text>
              </SummaryRow>
              <TotalRow>
                <Text strong variant="body2">
                  {t('Memberships:CANCEL_PLAN.REMAINING_COST')}
                </Text>
                <Text strong data-testid="remaining-cost" variant="body2">
                  {formatMoney(remainingCost)}
                </Text>
              </TotalRow>
            </CancellationSummarySection>
          </CancellationSummaryContainer>
        )}

        <Text strong mb={1} mt={3} variant="subheading3">
          {FieldsLabels.REASON_FOR_CANCELLATION}
        </Text>
        <ErrorTooltip message={reason.message} open={!reason.valid}>
          <FormControl fullWidth error={!reason.valid}>
            <OutlinedSelect
              id="cancel-reason-select"
              label={t('Memberships:CANCEL_PLAN.REASON_LABEL')}
              options={WellnessPlanCancelReasons}
              value={reason.value}
              onChange={(value) => reason.setValue(value)}
            />
          </FormControl>
        </ErrorTooltip>
        <Grid container item mt={1}>
          <PuiTextArea
            multiline
            field={notes}
            placeholder={FieldsLabels.CANCELLATION_NOTES}
          />
        </Grid>
      </Grid>
    )
  },
)

export default CancelMembershipDetails
